<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { z } from 'zod';
import { AdminReviewSchema, UiAside, UiCard } from '@shopexperts/shared';
import AdminReviewCard from '@/app/components/reviews/AdminReviewCard.vue';
import ApproveReviewModal from '@/app/components/reviews/ApproveReviewModal.vue';
import DeclineReviewModal from '@/app/components/reviews/DeclineReviewModal.vue';
import EditAdminReviewForm from '@/app/components/reviews/EditAdminReviewForm.vue';
import EmptyDataPlaceholder from '@/components/common/EmptyDataPlaceholder.vue';
import Spinner from '@/components/common/Spinner.vue';
import { useAdminPendingReviews } from '@/app/composables/reviews/useAdminPendingReviews';
import { usePublishReview } from '@/app/composables/reviews/usePublishReview';
import { useDeclineReview } from '@/app/composables/reviews/useDeclineReview';

type AdminReview = z.infer<typeof AdminReviewSchema>;

const {
  reviews,
  stats,
  fetchNextPage,
  hasNextPage,
  isFetching,
  isFetchingNextPage,
  isLoading,
} = useAdminPendingReviews();

const { publishReview, isPublishing } = usePublishReview();
const { declineReview, isDeclining } = useDeclineReview();

const showApproveModal = ref(false);
const showDeclineModal = ref(false);
const selectedReviewId = ref<number | null>(null);
const isEditAsideOpen = ref(false);
const selectedReview = ref<AdminReview | null>(null);

const sentinelRef = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

const handleEdit = (review: AdminReview) => {
  selectedReview.value = review;
  isEditAsideOpen.value = true;
};

const handleEditSuccess = () => {
  isEditAsideOpen.value = false;
  selectedReview.value = null;
};

const handleEditCancel = () => {
  isEditAsideOpen.value = false;
  selectedReview.value = null;
};

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (entry && entry.isIntersecting && hasNextPage.value && !isFetching.value) {
        fetchNextPage();
      }
    },
    {
      root: null,
      rootMargin: '100px',
      threshold: 0.1,
    }
  );

  if (sentinelRef.value) {
    observer.observe(sentinelRef.value);
  }
});

onUnmounted(() => {
  if (observer && sentinelRef.value) {
    observer.unobserve(sentinelRef.value);
  }
  observer?.disconnect();
});

const handleApprove = (id: number) => {
  selectedReviewId.value = id;
  showApproveModal.value = true;
};

const confirmApprove = (notes?: string) => {
  if (!selectedReviewId.value) return;

  publishReview(
    {
      reviewId: selectedReviewId.value,
      moderationNotes: notes,
    },
    {
      onSuccess: () => {
        showApproveModal.value = false;
        selectedReviewId.value = null;
      },
    }
  );
};

const cancelApprove = () => {
  showApproveModal.value = false;
  selectedReviewId.value = null;
};

const handleDecline = (id: number) => {
  selectedReviewId.value = id;
  showDeclineModal.value = true;
};

const confirmDecline = (notes?: string) => {
  if (!selectedReviewId.value) return;

  declineReview(
    {
      reviewId: selectedReviewId.value,
      moderationNotes: notes,
    },
    {
      onSuccess: () => {
        showDeclineModal.value = false;
        selectedReviewId.value = null;
      },
    }
  );
};

const cancelDecline = () => {
  showDeclineModal.value = false;
  selectedReviewId.value = null;
};

defineExpose({
  stats,
});
</script>

<template>
  <div class="space-y-4">
    <div v-if="isLoading" class="space-y-4">
      <UiCard
        v-for="i in 3"
        :key="i"
        padding="md"
        class="animate-pulse"
      >
        <div class="flex items-start gap-4">
          <div class="w-12 h-12 bg-gray-200 rounded-full"></div>
          <div class="flex-1 space-y-3">
            <div class="h-4 bg-gray-200 rounded w-1/4"></div>
            <div class="h-4 bg-gray-200 rounded w-3/4"></div>
            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </UiCard>
    </div>

    <EmptyDataPlaceholder
      v-else-if="!isLoading && reviews.length === 0"
      title="No Pending Reviews"
      description="There are no reviews waiting for approval at the moment."
    />

    <template v-else>
      <AdminReviewCard
        v-for="review in reviews"
        :key="review.id"
        :review="review"
        @edit="handleEdit"
        @approve="handleApprove"
        @decline="handleDecline"
      />

      <div
        ref="sentinelRef"
        class="h-10 flex items-center justify-center"
      >
        <Spinner v-if="isFetchingNextPage" />
      </div>
    </template>

    <ApproveReviewModal
      v-model="showApproveModal"
      :is-loading="isPublishing"
      @confirm="confirmApprove"
      @cancel="cancelApprove"
    />

    <DeclineReviewModal
      v-model="showDeclineModal"
      :is-loading="isDeclining"
      @confirm="confirmDecline"
      @cancel="cancelDecline"
    />

    <UiAside v-model="isEditAsideOpen" position="right" :close-on-overlay-click="false">
      <div class="p-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
        <EditAdminReviewForm
          v-if="selectedReview"
          :key="selectedReview.id"
          :review-id="selectedReview.id"
          :expert="{
            name: selectedReview.expert.name,
            type: selectedReview.expert.type,
            slug: selectedReview.expert.slug,
            profile_picture: selectedReview.expert.profile_picture,
            role: selectedReview.project?.name ?? selectedReview.expert.role ?? undefined,
          }"
          :initial-values="{
            quality: selectedReview.quality,
            communication: selectedReview.communication,
            timelines: selectedReview.timelines,
            value_for_money: selectedReview.value_for_money,
            review_text: selectedReview.review_text,
            project_value_range_id: selectedReview.project_value_range_id,
            is_anonymous: selectedReview.is_anonymous,
            hired_on_shopexperts_override: selectedReview.hired_on_shopexperts_override ?? (!selectedReview.project && (selectedReview.is_hired_on_shopexperts ?? false)),
          }"
          @success="handleEditSuccess"
          @cancel="handleEditCancel"
        />
      </div>
    </UiAside>
  </div>
</template>
